"use client";

import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import {
  GoogleDrivePreloader,
  PreloadStatus,
  PreloadConfig,
} from "@/lib/services/google-drive-preloader";
import type { CacheEntry, CacheKey, CacheState, GoogleDriveCacheContextType } from "./types";
import { cacheReducer } from "./reducer";
import { createCacheKey } from "./utils";

const initialState: CacheState = {
  entries: new Map(),
  maxSize: 100,
  defaultTTL: 5 * 60 * 1000,
};

export const GoogleDriveCacheContext = createContext<GoogleDriveCacheContextType | null>(null);

export function GoogleDriveCacheProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(cacheReducer, initialState);
  const preloaderRef = useRef<GoogleDrivePreloader | null>(null);
  const [preloadStatus, setPreloadStatus] = React.useState<PreloadStatus>({
    isPreloading: false,
    completed: 0,
    total: 0,
    results: [],
  });

  useEffect(() => {
    preloaderRef.current = new GoogleDrivePreloader({
      enabled: true,
      preloadSharedDrives: true,
      preloadRootFolders: true,
      maxConcurrentRequests: 2,
      delayBetweenRequests: 1000,
      timeoutPerRequest: 15000,
    });

    return () => {
      preloaderRef.current?.stopPreload();
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch({ type: "CLEAR_EXPIRED" });
    }, 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  const get = useCallback(
    (key: CacheKey): CacheEntry | null => {
      const keyString = createCacheKey(key);
      const entry = state.entries.get(keyString);

      if (!entry) return null;

      if (entry.expiresAt < Date.now()) {
        dispatch({ type: "INVALIDATE_ENTRY", key: keyString });
        return null;
      }

      return entry;
    },
    [state.entries]
  );

  const set = useCallback(
    (key: CacheKey, data: CacheEntry["data"]) => {
      const keyString = createCacheKey(key);
      const now = Date.now();
      const entry: CacheEntry = {
        data,
        timestamp: now,
        expiresAt: now + state.defaultTTL,
      };

      dispatch({ type: "SET_ENTRY", key: keyString, entry });
    },
    [state.defaultTTL]
  );

  const invalidate = useCallback((key: CacheKey) => {
    const keyString = createCacheKey(key);
    dispatch({ type: "INVALIDATE_ENTRY", key: keyString });
  }, []);

  const invalidatePattern = useCallback((pattern: Partial<CacheKey>) => {
    dispatch({ type: "INVALIDATE_PATTERN", pattern });
  }, []);

  const clear = useCallback(() => {
    dispatch({ type: "CLEAR_ALL" });
  }, []);

  const getCacheInfo = useCallback(() => {
    const entries = Array.from(state.entries.entries()).map(([key, entry]) => ({
      key,
      timestamp: entry.timestamp,
      expiresAt: entry.expiresAt,
    }));

    return {
      size: state.entries.size,
      maxSize: state.maxSize,
      entries,
    };
  }, [state.entries, state.maxSize]);

  const setConfig = useCallback((config: { maxSize?: number; defaultTTL?: number }) => {
    dispatch({ type: "SET_CONFIG", ...config });
  }, []);

  const startPreload = useCallback(
    async (accessToken: string) => {
      if (!preloaderRef.current || !accessToken) {
        console.warn(
          "🚀 Google Drive Preloader: Cannot start preload - missing preloader or token"
        );
        return;
      }

      try {
        const status = await preloaderRef.current.startPreload(
          accessToken,
          (cacheKey, data) => {
            set(cacheKey, data);
          },
          (status) => {
            setPreloadStatus(status);
          }
        );

        setPreloadStatus(status);
      } catch (error) {
        console.error("🚀 Google Drive Preloader: Error during preload:", error);
      }
    },
    [set]
  );

  const stopPreload = useCallback(() => {
    preloaderRef.current?.stopPreload();
    setPreloadStatus((prev) => ({ ...prev, isPreloading: false }));
  }, []);

  const getPreloadStatus = useCallback(() => {
    return preloaderRef.current?.getStatus() || preloadStatus;
  }, [preloadStatus]);

  const setPreloadConfig = useCallback((config: Partial<PreloadConfig>) => {
    preloaderRef.current?.updateConfig(config);
  }, []);

  const contextValue: GoogleDriveCacheContextType = useMemo(
    () => ({
      get,
      set,
      invalidate,
      invalidatePattern,
      clear,
      getCacheInfo,
      setConfig,
      startPreload,
      stopPreload,
      getPreloadStatus,
      setPreloadConfig,
    }),
    [
      get,
      set,
      invalidate,
      invalidatePattern,
      clear,
      getCacheInfo,
      setConfig,
      startPreload,
      stopPreload,
      getPreloadStatus,
      setPreloadConfig,
    ]
  );

  return (
    <GoogleDriveCacheContext.Provider value={contextValue}>
      {children}
    </GoogleDriveCacheContext.Provider>
  );
}

export function useGoogleDriveCache(): GoogleDriveCacheContextType {
  const context = useContext(GoogleDriveCacheContext);
  if (!context) {
    throw new Error("useGoogleDriveCache must be used within a GoogleDriveCacheProvider");
  }
  return context;
}

export function useGoogleDriveCacheDebug() {
  const cache = useGoogleDriveCache();

  return {
    ...cache.getCacheInfo(),
    clearCache: cache.clear,
    setTTL: (ttl: number) => cache.setConfig({ defaultTTL: ttl }),
    setMaxSize: (maxSize: number) => cache.setConfig({ maxSize }),
    invalidateAccount: (isSecondAccount: boolean) => cache.invalidatePattern({ isSecondAccount }),
    invalidateDrive: (driveId: string) => cache.invalidatePattern({ driveId }),
    invalidateAll: cache.clear,
  };
}
